(() => {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const productJsonCache = new Map();

  function loadProductJson(handle) {
    if (!handle) return Promise.reject(new Error("Missing product handle"));
    if (!productJsonCache.has(handle)) {
      productJsonCache.set(
        handle,
        fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: {
            Accept: "application/json",
          },
        }).then((response) => {
          if (!response.ok) throw new Error("Unable to load product");
          return response.json();
        }),
      );
    }

    return productJsonCache.get(handle);
  }

  function formatMoney(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return "";
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(amount);
    }

    const currency = window.Shopify?.currency?.active || "USD";
    return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency,
    }).format(amount / 100);
  }

  function updateInventoryText(item, product, variantId) {
    const count = item?.querySelector("[data-nept-inventory-count]");
    if (!item || !count) return;

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const variant = variants.find((entry) => String(entry.id) === String(variantId)) || variants[0];
    const quantity = Number(variant?.inventory_quantity);
    if (Number.isFinite(quantity)) count.textContent = String(Math.max(0, quantity));
  }

  function updatePriceText(item, product, variantId) {
    const amount = item?.querySelector("[data-nept-price-amount]");
    if (!item || !amount) return;

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const variant = variants.find((entry) => String(entry.id) === String(variantId)) || variants[0];
    const price = Number(variant?.price);
    if (Number.isFinite(price)) amount.textContent = formatMoney(price);
  }

  function initCursor() {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    document.querySelectorAll(".nept-cursor").forEach((item, index) => {
      if (index > 0) item.remove();
    });

    let cursor = document.querySelector(".nept-cursor");
    if (!cursor) {
      cursor = document.createElement("div");
      cursor.className = "nept-cursor";
      cursor.setAttribute("aria-hidden", "true");
      document.body.appendChild(cursor);
    }
    document.body.classList.add("nept-cursor-enabled");

    const clickableSelector = 'a, button, [role="button"], input[type="submit"], [onclick]';
    const isClickable = (target) =>
      target instanceof Element &&
      (Boolean(target.closest(clickableSelector)) || window.getComputedStyle(target).cursor === "pointer");

    const updateClickableState = (target) => {
      cursor.classList.toggle("is-clickable", isClickable(target));
    };

    window.addEventListener("pointermove", (event) => {
      cursor.style.transform = `translate3d(${event.clientX}px, ${event.clientY}px, 0) translate(-50%, -50%)`;
      cursor.classList.add("is-active");
      updateClickableState(event.target);
    });

    document.addEventListener("pointerover", (event) => {
      updateClickableState(event.target);
    });

    document.addEventListener("pointerout", (event) => {
      updateClickableState(event.relatedTarget);
    });

    document.addEventListener("pointerleave", () => {
      cursor.classList.remove("is-active");
      cursor.classList.remove("is-clickable");
    });
  }

  function initReveal() {
    const items = document.querySelectorAll(".nept-reveal");
    if (!items.length) return;

    if (reducedMotion || !("IntersectionObserver" in window)) {
      items.forEach((item) => item.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const delay = entry.target.dataset.revealDelay || 0;
          entry.target.style.transitionDelay = `${delay}ms`;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.16 },
    );

    items.forEach((item) => observer.observe(item));
  }

  function initHeroWords() {
    document.querySelectorAll("[data-nept-hero-word]").forEach((word, index) => {
      if (reducedMotion) {
        word.style.opacity = "1";
        word.style.transform = "none";
        return;
      }

      word.animate(
        [
          { opacity: 0, transform: "translateY(1.2em)" },
          { opacity: 1, transform: "translateY(0)" },
        ],
        {
          delay: 140 + index * 95,
          duration: 820,
          easing: "cubic-bezier(.2,.85,.2,1)",
          fill: "forwards",
        },
      );
    });
  }

  function initCardTilt() {
    document.querySelectorAll(".nept-card-tilt").forEach((card) => {
      card.addEventListener("mousemove", (event) => {
        const rect = card.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;
        const rotateX = -(y / rect.height) * 6;
        const rotateY = (x / rect.width) * 6;
        card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
        card.style.transition = "transform 0.15s ease-out";
        card.style.boxShadow = "0 0 40px rgba(255,255,255,0.06), 0 0 80px rgba(255,255,255,0.03)";
      });
      card.addEventListener("mouseleave", () => {
        card.style.transform = "perspective(800px) rotateX(0deg) rotateY(0deg)";
        card.style.transition = "transform 0.6s ease-out, box-shadow 0.6s ease-out";
        card.style.boxShadow = "none";
      });
    });
  }

  function initDropImagePaint() {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    document.querySelectorAll("[data-nept-drop-paint]").forEach((media) => {
      const image = media.querySelector(".nept-drop__image--mono");
      const canvas = media.querySelector("[data-nept-drop-paint-canvas]");
      const reset = media.closest(".nept-drop__visual")?.querySelector("[data-nept-drop-paint-reset]");
      if (!image || !canvas) return;

      const context = canvas.getContext("2d");
      const colorCanvas = document.createElement("canvas");
      const colorContext = colorCanvas.getContext("2d");
      let lastPoint = null;

      context.imageSmoothingEnabled = false;
      colorContext.imageSmoothingEnabled = false;

      const setupCanvas = (clearPaint = false) => {
        if (!image.complete || !image.naturalWidth) return;
        const width = image.naturalWidth;
        const height = image.naturalHeight;
        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width;
          canvas.height = height;
          colorCanvas.width = width;
          colorCanvas.height = height;
          context.imageSmoothingEnabled = false;
          colorContext.imageSmoothingEnabled = false;
          lastPoint = null;
        }

        if (clearPaint) {
          context.clearRect(0, 0, canvas.width, canvas.height);
          lastPoint = null;
        }

        colorContext.clearRect(0, 0, colorCanvas.width, colorCanvas.height);
        colorContext.drawImage(image, 0, 0);
      };

      const getPaintPoint = (event) => {
        const rect = media.getBoundingClientRect();
        const coverScale = Math.max(rect.width / canvas.width, rect.height / canvas.height);
        const renderedWidth = canvas.width * coverScale;
        const renderedHeight = canvas.height * coverScale;
        const offsetX = (rect.width - renderedWidth) / 2;
        const offsetY = (rect.height - renderedHeight) / 2;

        return {
          x: (event.clientX - rect.left - offsetX) / coverScale,
          y: (event.clientY - rect.top - offsetY) / coverScale,
          radius: 120 / coverScale,
        };
      };

      const stampBrush = (x, y, radius) => {
        const brushRadius = Math.max(1, Math.round(radius));
        const size = brushRadius * 2;
        const left = Math.round(x) - brushRadius;
        const top = Math.round(y) - brushRadius;
        const stampCanvas = document.createElement("canvas");
        const stampContext = stampCanvas.getContext("2d");
        stampCanvas.width = size;
        stampCanvas.height = size;
        stampContext.imageSmoothingEnabled = false;

        stampContext.drawImage(colorCanvas, -left, -top);

        const gradient = stampContext.createRadialGradient(brushRadius, brushRadius, brushRadius * 0.12, brushRadius, brushRadius, brushRadius);
        gradient.addColorStop(0, "rgba(255,255,255,1)");
        gradient.addColorStop(0.46, "rgba(255,255,255,1)");
        gradient.addColorStop(0.78, "rgba(255,255,255,0.42)");
        gradient.addColorStop(1, "rgba(255,255,255,0)");

        stampContext.globalCompositeOperation = "destination-in";
        stampContext.fillStyle = gradient;
        stampContext.fillRect(0, 0, size, size);
        context.drawImage(stampCanvas, left, top);
      };

      const paintTo = (event) => {
        if (!canvas.width || !canvas.height) setupCanvas();
        if (!canvas.width || !canvas.height) return;
        const point = getPaintPoint(event);
        const previous = lastPoint || point;
        const distance = Math.hypot(point.x - previous.x, point.y - previous.y);
        const steps = Math.max(1, Math.ceil(distance / (point.radius * 0.18)));

        for (let step = 0; step <= steps; step += 1) {
          const progress = step / steps;
          stampBrush(
            previous.x + (point.x - previous.x) * progress,
            previous.y + (point.y - previous.y) * progress,
            point.radius,
          );
        }

        lastPoint = point;
      };

      const resetPaint = () => {
        context.clearRect(0, 0, canvas.width, canvas.height);
        lastPoint = null;
      };

      if (image.complete) setupCanvas();
      image.addEventListener("load", () => setupCanvas(true));
      media.addEventListener("pointermove", paintTo);
      media.addEventListener("pointerleave", () => {
        lastPoint = null;
      });
      reset?.addEventListener("click", resetPaint);
    });
  }

  function initLiveInventory() {
    document.querySelectorAll("[data-nept-live-inventory]").forEach((item) => {
      const handle = item.dataset.neptProductHandle;
      const variantId = item.dataset.neptVariantId;
      const count = item.querySelector("[data-nept-inventory-count]");
      if (!handle || !count) return;

      loadProductJson(handle)
        .then((product) => updateInventoryText(item, product, variantId))
        .catch(() => {
          // Keep the Liquid-rendered fallback count if Shopify Ajax inventory is unavailable.
        });
    });

    document.querySelectorAll("[data-nept-live-price]").forEach((item) => {
      const handle = item.dataset.neptProductHandle;
      const variantId = item.dataset.neptVariantId;
      if (!handle) return;

      loadProductJson(handle)
        .then((product) => updatePriceText(item, product, variantId))
        .catch(() => {
          // Keep the Liquid-rendered fallback price if Shopify Ajax is unavailable.
        });
    });
  }

  function initProductPage() {
    document.querySelectorAll("[data-nept-product]").forEach((section) => {
      const handle = section.dataset.neptProductHandle;
      const primaryImage = section.querySelector("[data-nept-product-primary-image]");
      const gallery = section.querySelector("[data-nept-product-gallery]");
      const imagesData = section.querySelector("[data-nept-product-images]");
      const variantsData = section.querySelector("[data-nept-product-variants]");
      const prevButton = section.querySelector("[data-nept-product-gallery-prev]");
      const nextButton = section.querySelector("[data-nept-product-gallery-next]");
      const imageIndex = section.querySelector("[data-nept-product-image-index]");
      const imageTotal = section.querySelector("[data-nept-product-image-total]");
      const inventory = section.querySelector("[data-nept-live-inventory]");
      const variantInput = section.querySelector("[data-nept-product-variant-id]");
      const price = section.querySelector("[data-nept-product-price]");
      const addButton = section.querySelector("[data-nept-add-to-cart]");
      const addLabel = section.querySelector("[data-nept-add-to-cart-label]");
      const parseJson = (script, fallback) => {
        if (!script?.textContent) return fallback;
        try {
          return JSON.parse(script.textContent);
        } catch {
          return fallback;
        }
      };
      const productImages = parseJson(imagesData, []);
      const variantMeta = new Map(parseJson(variantsData, []).map((variant) => [String(variant.id), variant]));
      let currentImageIndex = 0;
      let productData = null;

      const padNumber = (value) => String(value).padStart(2, "0");

      const renderImage = (index) => {
        if (!primaryImage || !productImages.length) return;
        currentImageIndex = (index + productImages.length) % productImages.length;
        const image = productImages[currentImageIndex];
        primaryImage.src = image.src || primaryImage.src;
        primaryImage.srcset = image.srcset || "";
        primaryImage.alt = image.alt || primaryImage.alt;
        if (gallery && image.ratio) gallery.style.setProperty("--nept-product-image-ratio", image.ratio);
        if (imageIndex) imageIndex.textContent = padNumber(currentImageIndex + 1);
        if (imageTotal) imageTotal.textContent = padNumber(productImages.length);
      };

      prevButton?.addEventListener("click", () => renderImage(currentImageIndex - 1));
      nextButton?.addEventListener("click", () => renderImage(currentImageIndex + 1));
      if (productImages.length) renderImage(0);

      const selectedOptions = () => {
        const options = [];
        section.querySelectorAll("[data-nept-product-option]:checked").forEach((option) => {
          options[Number(option.dataset.optionPosition) - 1] = option.value;
        });
        return options;
      };

      const findSelectedVariant = () => {
        const options = selectedOptions();
        const variants = Array.isArray(productData?.variants) ? productData.variants : [];
        return variants.find((variant) => variant.options.every((value, index) => options[index] === value));
      };

      const applyVariant = (variant) => {
        if (!variant) {
          if (addButton) addButton.disabled = true;
          if (addLabel) addLabel.textContent = "Unavailable";
          return;
        }

        if (variantInput) variantInput.value = variant.id;
        if (inventory) {
          inventory.dataset.neptVariantId = variant.id;
          const inventoryCount = Number(variantMeta.get(String(variant.id))?.inventory);
          if (Number.isFinite(inventoryCount)) {
            const count = inventory.querySelector("[data-nept-inventory-count]");
            if (count) count.textContent = String(Math.max(0, inventoryCount));
          } else {
            updateInventoryText(inventory, productData, variant.id);
          }
        }
        if (price) price.textContent = formatMoney(variant.price);
        if (addButton) addButton.disabled = !variant.available;
        if (addLabel) addLabel.textContent = variant.available ? "Add to cart" : "Sold out";

        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      };

      section.querySelectorAll("[data-nept-product-option]").forEach((option) => {
        option.addEventListener("change", () => {
          if (productData) applyVariant(findSelectedVariant());
        });
      });

      loadProductJson(handle)
        .then((product) => {
          productData = product;
          const variants = Array.isArray(product.variants) ? product.variants : [];
          const current = variants.find((variant) => String(variant.id) === String(variantInput?.value));
          applyVariant(current || findSelectedVariant() || variants[0]);
        })
        .catch(() => {
          // Product form remains usable with the Liquid-rendered variant if Ajax is unavailable.
        });
    });
  }

  function initNewsletterPopup() {
    const openers = document.querySelectorAll("[data-nept-newsletter-open]");
    const overlay = document.querySelector("[data-nept-newsletter]");
    if (!openers.length || !overlay) return;

    const close = overlay.querySelector("[data-nept-newsletter-close]");
    const email = overlay.querySelector('input[type="email"]');

    const openPopup = () => {
      overlay.classList.add("is-open");
      overlay.setAttribute("aria-hidden", "false");
      window.setTimeout(() => email?.focus(), 60);
    };

    const closePopup = () => {
      overlay.classList.remove("is-open");
      overlay.setAttribute("aria-hidden", "true");
    };

    openers.forEach((button) => button.addEventListener("click", openPopup));
    close?.addEventListener("click", closePopup);
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) closePopup();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closePopup();
    });
  }

  function initScrollProgress() {
    const progress = document.querySelector("[data-nept-scroll-progress]");
    if (!progress) return;
    const clip = progress.querySelector(".nept-scroll-progress__clip");

    let frame = null;

    const update = () => {
      frame = null;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const percent = scrollable > 0 ? Math.round((window.scrollY / scrollable) * 100) : 0;
      const clamped = Math.min(100, Math.max(0, percent));
      progress.style.setProperty("--nept-scroll-progress", `${clamped}%`);
      clip?.setAttribute("height", String((48 * clamped) / 100));
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
  }

  function initIdentityTextWipe() {
    const line = document.querySelector(".nept-story__body p:nth-child(2)");
    if (!line) return;

    line.setAttribute("data-nept-wipe-text", line.textContent.trim());

    let frame = null;

    const update = () => {
      frame = null;
      const rect = line.getBoundingClientRect();
      const start = window.innerHeight * 0.88;
      const end = window.innerHeight * 0.35;
      const raw = (start - rect.top) / (start - end);
      const progress = Math.min(1, Math.max(0, raw));
      line.style.setProperty("--nept-story-wipe", `${100 - progress * 100}%`);
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
  }

  document.addEventListener("DOMContentLoaded", () => {
    initCursor();
    initReveal();
    initHeroWords();
    initCardTilt();
    initDropImagePaint();
    initLiveInventory();
    initProductPage();
    initNewsletterPopup();
    initScrollProgress();
    initIdentityTextWipe();
  });
})();
